import { useEffect, useRef } from 'react';
import styles from './Dice3D.module.css';

/**
 * Props for the Dice3D component.
 */
interface Dice3DProps {
  /** Final dice value (1-6). Determines which face is shown when rolling stops. */
  value: number;
  /** When true, the cube tumbles rapidly. When false, it rotates to show `value`. */
  rolling: boolean;
  /** Optional callback fired when the rolling animation completes. */
  onRollEnd?: () => void;
}

/**
 * Maps a dice value (1-6) to the CSS transform that brings that face to the
 * front of the cube.
 *
 * Cube face layout (opposite faces sum to 7):
 *   Front=1  Back=6  Right=3  Left=4  Top=2  Bottom=5
 *
 * To bring a face to the front we rotate the *cube* in the opposite direction
 * of the face's own positioning transform.
 */
const FACE_TRANSFORMS: Record<number, string> = {
  1: 'rotateX(0deg) rotateY(0deg)',
  2: 'rotateX(-90deg) rotateY(0deg)',
  3: 'rotateX(0deg) rotateY(-90deg)',
  4: 'rotateX(0deg) rotateY(90deg)',
  5: 'rotateX(90deg) rotateY(0deg)',
  6: 'rotateX(0deg) rotateY(180deg)',
};

/**
 * Dot positions for each face value, using a 3×3 grid index (0-8):
 *   0 1 2
 *   3 4 5
 *   6 7 8
 */
const DOT_POSITIONS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

/**
 * Renders a single dice face with dots arranged according to `value`.
 *
 * @param value - The dice value (1-6) for this face.
 * @param faceClassName - CSS module class for the face's 3D position.
 * @returns A JSX element representing the face.
 */
function renderFace(value: number, faceClassName: string): JSX.Element {
  const positions = DOT_POSITIONS[value] || [];
  return (
    <div className={`${styles.face} ${faceClassName}`}>
      <div className={styles.dotGrid}>
        {Array.from({ length: 9 }, (_, i) => (
          <div
            key={i}
            className={`${styles.dot} ${positions.includes(i) ? styles.dotActive : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A 3D dice rendered with pure CSS 3D transforms (no WebGL / three.js).
 *
 * The cube has six faces, each showing a dice value (1-6). When `rolling` is
 * true a CSS keyframe animation tumbles the cube. When `rolling` becomes false
 * the cube smoothly transitions (0.8s ease-out) to the rotation that shows the
 * `value` face.
 *
 * The colour scheme matches the board's dark theme: faces use `#232342` and
 * dots are white.
 */
export default function Dice3D({ value, rolling, onRollEnd }: Dice3DProps): JSX.Element {
  const onRollEndRef = useRef(onRollEnd);
  onRollEndRef.current = onRollEnd;

  // When rolling transitions from true → false, fire onRollEnd after the
  // 0.8s transition completes.
  const prevRollingRef = useRef(rolling);
  useEffect(() => {
    if (prevRollingRef.current && !rolling) {
      const timer = setTimeout(() => {
        onRollEndRef.current?.();
      }, 800);
      return () => clearTimeout(timer);
    }
    prevRollingRef.current = rolling;
  }, [rolling]);

  const safeValue = Math.max(1, Math.min(6, Math.round(value)));
  const targetTransform = FACE_TRANSFORMS[safeValue] || FACE_TRANSFORMS[1];

  return (
    <div className={styles.scene}>
      <div
        className={`${styles.cube} ${rolling ? styles.rolling : ''}`}
        style={rolling ? undefined : { transform: targetTransform }}
      >
        {renderFace(1, styles.face1)}
        {renderFace(2, styles.face2)}
        {renderFace(3, styles.face3)}
        {renderFace(4, styles.face4)}
        {renderFace(5, styles.face5)}
        {renderFace(6, styles.face6)}
      </div>
    </div>
  );
}
